import type {
  AcpElicitationContentValue,
  AcpElicitationOutcome,
  AcpElicitationPropertySchema,
  AcpElicitationRequest,
} from '@crowdex/agent-contracts';
import { Button } from '@crowdex/ui/components/button';
import { Chip } from '@crowdex/ui/components/chip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@crowdex/ui/components/dialog';
import { Input } from '@crowdex/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@crowdex/ui/components/select';
import { cn } from '@crowdex/ui/lib/utils';
import { ExternalLink, FormInput } from 'lucide-react';
import { type FormEvent, type ReactNode, useMemo, useState } from 'react';
import { SHEET_ON_PHONE } from '@/components/desktop-shell/dialog-sheet';

type FormValues = Record<string, AcpElicitationContentValue>;
type EnumChoice = { const: string; title: string; description?: string | undefined };
type FormField = {
  entry: [string, AcpElicitationPropertySchema];
  other?: [string, AcpElicitationPropertySchema];
};

/** A choice among this many options or fewer shows as a segmented control, more as a menu. */
const SEGMENTED_OPTIONS = 4;
const NONE = '__none__';

function defaultsFor(request: AcpElicitationRequest): FormValues {
  if (request.mode !== 'form') return {};
  return Object.fromEntries(
    Object.entries(request.requestedSchema.properties ?? {}).flatMap(([name, property]) => {
      if (!('default' in property) || property.default == null) return [];
      return [[name, property.default as AcpElicitationContentValue]];
    }),
  );
}

function enumOptions(property: Record<string, unknown>): EnumChoice[] {
  const source =
    property['type'] === 'array' &&
    property['items'] !== null &&
    typeof property['items'] === 'object'
      ? (property['items'] as Record<string, unknown>)
      : property;
  const titled = source['oneOf'] ?? source['anyOf'];
  if (Array.isArray(titled)) {
    return titled.flatMap((option) => {
      if (option === null || typeof option !== 'object') return [];
      const record = option as Record<string, unknown>;
      return typeof record['const'] === 'string' && typeof record['title'] === 'string'
        ? [
            {
              const: record['const'],
              title: record['title'],
              description:
                typeof record['description'] === 'string' ? record['description'] : undefined,
            },
          ]
        : [];
    });
  }
  return Array.isArray(source['enum'])
    ? source['enum'].flatMap((value) =>
        typeof value === 'string' ? [{ const: value, title: value }] : [],
      )
    : [];
}

function safeExternalUrl(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch {
    return null;
  }
}

function isOtherProperty(property: Record<string, unknown>): boolean {
  return property['type'] === 'string' && property['title'] === 'Other';
}

function groupFields(entries: [string, AcpElicitationPropertySchema][]): FormField[] {
  const grouped: FormField[] = [];
  for (const entry of entries) {
    const previous = grouped.at(-1);
    if (
      previous !== undefined &&
      previous.other === undefined &&
      enumOptions(previous.entry[1] as Record<string, unknown>).length > 0 &&
      isOtherProperty(entry[1] as Record<string, unknown>)
    ) {
      previous.other = entry;
    } else {
      grouped.push({ entry });
    }
  }
  return grouped;
}

const isEmpty = (value: AcpElicitationContentValue | undefined) =>
  value === undefined ||
  (typeof value === 'string' && value.length === 0) ||
  (Array.isArray(value) && value.length === 0);

/** Who asks: the tool's name its schema gives ("Linear"), when it gives one. */
export function elicitationSource(request: AcpElicitationRequest): string | null {
  if (request.mode !== 'form') return null;
  const title = (request.requestedSchema as Record<string, unknown>)['title'];
  return typeof title === 'string' && title.trim() !== '' ? title.trim() : null;
}

/**
 * A form a tool asks the user to fill (design D50, mobile 48): a dialog (a sheet on a phone)
 * with every field it asks at once — a few choices as a segmented control, more in a menu, text
 * and numbers as fields — then Decline or Send. Closed without answering, it waits: its card
 * in the transcript opens it again.
 */
export function AgentElicitationDialog({
  request,
  pendingCount,
  onAnswer,
  open = true,
  onOpenChange,
}: {
  request: AcpElicitationRequest;
  pendingCount: number;
  onAnswer(outcome: AcpElicitationOutcome): void;
  open?: boolean;
  /** Closing it without an answer: it stays pending. Without it, closing declines. */
  onOpenChange?: (open: boolean) => void;
}) {
  const fields = useMemo<FormField[]>(
    () =>
      request.mode === 'form'
        ? groupFields(Object.entries(request.requestedSchema.properties ?? {}))
        : [],
    [request],
  );
  const [values, setValues] = useState<FormValues>(() => defaultsFor(request));
  const [validationError, setValidationError] = useState<string | null>(null);
  const required = useMemo(
    () => new Set(request.mode === 'form' ? (request.requestedSchema.required ?? []) : []),
    [request],
  );
  const externalUrl = request.mode === 'url' ? safeExternalUrl(request.url) : null;
  const source = elicitationSource(request);

  const update = (name: string, value: AcpElicitationContentValue | undefined) => {
    setValues((current) => {
      const next = { ...current };
      if (value === undefined) delete next[name];
      else next[name] = value;
      return next;
    });
    setValidationError(null);
  };

  const validate = (): boolean => {
    for (const field of fields) {
      const [name, property] = field.entry;
      const schema = property as Record<string, unknown>;
      const label = typeof schema['title'] === 'string' ? schema['title'] : name;
      const value = values[name];
      const other = field.other === undefined ? undefined : values[field.other[0]];
      if (required.has(name) && isEmpty(value) && isEmpty(other)) {
        setValidationError(`${label} is required.`);
        return false;
      }
      if (Array.isArray(value)) {
        const minimum = typeof schema['minItems'] === 'number' ? schema['minItems'] : null;
        const maximum = typeof schema['maxItems'] === 'number' ? schema['maxItems'] : null;
        if (minimum !== null && value.length < minimum) {
          setValidationError(`Pick at least ${minimum} for ${label}.`);
          return false;
        }
        if (maximum !== null && value.length > maximum) {
          setValidationError(`Pick at most ${maximum} for ${label}.`);
          return false;
        }
      }
    }
    return true;
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (request.mode === 'url') {
      onAnswer({ action: 'accept' });
      return;
    }
    if (validate()) onAnswer({ action: 'accept', content: values });
  };

  const renderField = (field: FormField) => {
    const [name, property] = field.entry;
    const otherField = field.other;
    const schema = property as Record<string, unknown>;
    const label = typeof schema['title'] === 'string' ? schema['title'] : name;
    const description = typeof schema['description'] === 'string' ? schema['description'] : null;
    const fieldType = typeof schema['type'] === 'string' ? schema['type'] : 'unknown';
    const options = enumOptions(schema);
    const id = `elicitation-${request.requestId}-${name}`;
    const otherOn = otherField !== undefined && Object.hasOwn(values, otherField[0]);

    const pick = (value: string) => {
      setValues((current) => {
        const next = { ...current, [name]: value };
        if (otherField !== undefined) delete next[otherField[0]];
        return next;
      });
      setValidationError(null);
    };

    let control: ReactNode;
    if (fieldType === 'boolean') {
      control = (
        <Segmented
          label={label}
          onPick={(value) => update(name, value === 'true')}
          options={[
            { const: 'true', title: 'Yes' },
            { const: 'false', title: 'No' },
          ]}
          value={typeof values[name] === 'boolean' ? String(values[name]) : null}
        />
      );
    } else if (fieldType === 'array') {
      const selected = Array.isArray(values[name]) ? (values[name] as string[]) : [];
      control = (
        <fieldset aria-label={label} className="flex flex-wrap gap-1.5">
          {options.map((option) => {
            const on = selected.includes(option.const);
            return (
              <Chip
                key={option.const}
                onClick={() =>
                  update(
                    name,
                    on
                      ? selected.filter((value) => value !== option.const)
                      : [...selected, option.const],
                  )
                }
                selected={on}
                size="md"
                title={option.description}
              >
                {option.title}
              </Chip>
            );
          })}
        </fieldset>
      );
    } else if (options.length > 0) {
      const choices =
        otherField === undefined ? options : [...options, { const: NONE, title: 'Other' }];
      const value = otherOn ? NONE : typeof values[name] === 'string' ? values[name] : null;
      const onPick = (next: string) => {
        if (next === NONE && otherField !== undefined) {
          setValues((current) => {
            const updated = { ...current, [otherField[0]]: '' };
            delete updated[name];
            return updated;
          });
          return;
        }
        pick(next);
      };
      control =
        choices.length <= SEGMENTED_OPTIONS ? (
          <Segmented label={label} onPick={onPick} options={choices} value={value} />
        ) : (
          <Select onValueChange={onPick} value={value ?? ''}>
            <SelectTrigger
              aria-label={label}
              className="h-control-lg w-full rounded-xl bg-control"
              id={id}
            >
              <SelectValue placeholder={required.has(name) ? 'Choose…' : 'Nobody'} />
            </SelectTrigger>
            <SelectContent>
              {choices.map((option) => (
                <SelectItem key={option.const} value={option.const}>
                  {option.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
    } else if (fieldType === 'number' || fieldType === 'integer') {
      control = (
        <Input
          className="h-control-lg rounded-xl bg-control"
          id={id}
          max={typeof schema['maximum'] === 'number' ? schema['maximum'] : undefined}
          min={typeof schema['minimum'] === 'number' ? schema['minimum'] : undefined}
          onChange={(event) =>
            update(name, event.target.value === '' ? undefined : event.target.valueAsNumber)
          }
          step={fieldType === 'integer' ? 1 : 'any'}
          type="number"
          value={typeof values[name] === 'number' ? values[name] : ''}
        />
      );
    } else if (fieldType === 'string') {
      control = (
        <Input
          className="h-control-lg rounded-xl bg-control"
          id={id}
          maxLength={typeof schema['maxLength'] === 'number' ? schema['maxLength'] : undefined}
          minLength={typeof schema['minLength'] === 'number' ? schema['minLength'] : undefined}
          onChange={(event) => update(name, event.target.value)}
          pattern={typeof schema['pattern'] === 'string' ? schema['pattern'] : undefined}
          type={
            schema['format'] === 'email'
              ? 'email'
              : schema['format'] === 'uri'
                ? 'url'
                : schema['format'] === 'date'
                  ? 'date'
                  : schema['format'] === 'date-time'
                    ? 'datetime-local'
                    : 'text'
          }
          value={typeof values[name] === 'string' ? values[name] : ''}
        />
      );
    } else {
      control = (
        <p className="text-destructive text-sm">This field is not supported: {fieldType}</p>
      );
    }

    return (
      <div className="grid gap-1.5" key={name}>
        <label className="text-foreground text-sm" htmlFor={id}>
          {label}
          {!required.has(name) && <span className="text-muted-foreground"> (optional)</span>}
        </label>
        {description !== null && <p className="type-meta text-muted-foreground">{description}</p>}
        {control}
        {otherOn && otherField !== undefined && (
          <Input
            aria-label={`${label}: other`}
            autoFocus
            className="h-control-lg rounded-xl bg-control"
            onChange={(event) => update(otherField[0], event.target.value)}
            placeholder="Type an answer…"
            value={String(values[otherField[0]] ?? '')}
          />
        )}
      </div>
    );
  };

  return (
    <Dialog
      onOpenChange={(next) => {
        if (next) return;
        if (onOpenChange !== undefined) onOpenChange(false);
        else onAnswer({ action: 'decline' });
      }}
      open={open}
    >
      <DialogContent className={cn('gap-4 sm:max-w-md', SHEET_ON_PHONE)} data-elicitation-dialog>
        <form className="grid gap-4" onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="type-card-title">
              {source === null ? 'The agent needs details' : `${source} needs details`}
            </DialogTitle>
            <DialogDescription className="whitespace-pre-wrap">{request.message}</DialogDescription>
          </DialogHeader>
          {request.mode === 'url' ? (
            externalUrl === null ? (
              <p className="rounded-xl bg-destructive/10 p-3 text-destructive text-sm">
                The agent gave an unsafe or invalid address.
              </p>
            ) : (
              <Button asChild className="w-full rounded-control" variant="outline">
                <a href={externalUrl} rel="noreferrer" target="_blank">
                  <ExternalLink className="size-4" />
                  Open the page
                </a>
              </Button>
            )
          ) : (
            fields.map(renderField)
          )}
          {validationError !== null && (
            <p className="text-destructive text-sm" role="alert">
              {validationError}
            </p>
          )}
          <DialogFooter className="items-center gap-2 border-hairline border-t pt-4 max-md:flex-row">
            <p className="type-meta mr-auto flex items-center gap-1.5 text-muted-foreground max-md:hidden">
              <FormInput aria-hidden className="size-3.5" />
              {source === null ? 'Asked by a tool' : `Asked by the ${source} tool`}
              {pendingCount > 1 ? ` · ${pendingCount - 1} more waiting` : ''}
            </p>
            <Button
              className="rounded-control max-md:h-12 max-md:flex-1"
              onClick={() => onAnswer({ action: 'decline' })}
              type="button"
              variant="outline"
            >
              Decline
            </Button>
            <Button
              className="rounded-control max-md:h-12 max-md:flex-1"
              disabled={request.mode === 'url' && externalUrl === null}
              type="submit"
            >
              Send
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** A choice among a few, as one control: Low · Medium · High · Urgent. */
function Segmented({
  label,
  options,
  value,
  onPick,
}: {
  label: string;
  options: readonly EnumChoice[];
  value: string | null;
  onPick: (value: string) => void;
}) {
  return (
    <fieldset
      aria-label={label}
      className="grid auto-cols-fr grid-flow-col gap-1 rounded-xl bg-field p-1"
    >
      {options.map((option) => (
        <button
          aria-pressed={value === option.const}
          className={cn(
            'h-8 rounded-lg border px-2 text-sm transition-colors',
            // The chosen one: the shared selected state (SELECTED_CONTROL), on a recessed track.
            value === option.const
              ? 'border-input bg-control font-medium text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground',
          )}
          key={option.const}
          onClick={() => onPick(option.const)}
          title={option.description}
          type="button"
        >
          {option.title}
        </button>
      ))}
    </fieldset>
  );
}
